import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Check, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { hasMarketingAccess, hasCrmAccess } from "../../utils/org-role";

export type SuiteId = "marketing" | "automate" | "crm" | "unify" | "track" | "cxo";

interface SuiteInfo {
  id: SuiteId;
  label: string;
  description: string;
  // null = no pages built yet — listed but disabled with a "coming soon" notice.
  path: string | null;
}

// Shared suite list backing the switcher dropdown in every sidebar header
// (Marketing, CRM, Unify, Track). Each sidebar renders this same component
// with its own `activeSuite` rather than duplicating the list.
export const SUITES: SuiteInfo[] = [
  {
    id: "marketing",
    label: "Marketing",
    description:
      "Drive growth with campaigns & journeys Campaign Management, Journey Builder, A/B Testing, Audience Segmentation, Analytics",
    path: "/cpas-dashboard",
  },
  {
    id: "automate",
    label: "Automate",
    description:
      "Automate any workflow, not just campaigns  Workflow Builder, Trigger Library, AI Automation Nodes, Approval & Escalation Rules",
    path: "/automate/bot-studio",
  },
  {
    id: "crm",
    label: "CRM",
    description: "Manage relationships & pipeline  Unified Inbox, Contact Management, Sales Pipeline, Activity Tracking, Reporting",
    path: "/crm/dashboard",
  },
  {
    id: "unify",
    label: "Unify",
    description:
      "Unify every customer signal (CDP)  Identity Resolution, Unified Customer Profile, Data Connectors, Consent Management, Audience Sync",
    path: "/unify/overview",
  },
  {
    id: "track",
    label: "Track",
    description: "Capture events from every touchpoint  Website/App SDKs, Event Schema, API Keys, Server-side Events, Debug Console",
    path: "/track/overview",
  },
  {
    id: "cxo",
    label: "CXO",
    description: "Cross-module KPIs, one dashboard, full navigation access",
    path: "/cxo/overview",
  },
];

interface SuiteSwitcherProps {
  activeSuite: SuiteId;
  // Sidebar's own open/collapsed state — when collapsed, the trigger expands
  // the sidebar first (same timing pattern used for nested nav groups) before
  // opening the dropdown, since the panel needs the full width to render.
  open: boolean;
  onExpandSidebar: () => void;
  role?: string;
  orgRole?: string | null;
}

const SuiteSwitcher: React.FC<SuiteSwitcherProps> = ({ activeSuite, open, onExpandSidebar, role = "", orgRole }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Sidebar headers live inside a `sticky`/`fixed` <aside> that establishes
  // its own stacking context, so no z-index on an absolutely-positioned
  // child can ever paint above the page's <main> content — the whole aside
  // is one layer, painted below main regardless. Portal the panel straight
  // onto <body> and position it with fixed coordinates instead, so it's
  // guaranteed to render above everything (matching the "nothing above it"
  // reference).
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const active = SUITES.find((s) => s.id === activeSuite) || SUITES[0];

  const updateCoords = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setCoords({ top: rect.bottom + 8, left: rect.left });
  };

  useLayoutEffect(() => {
    if (dropdownOpen) updateCoords();
  }, [dropdownOpen]);

  useEffect(() => {
    if (!dropdownOpen) return;
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Marketing/CRM keep the same role gates their old single-target switch
  // buttons used; Unify/Track have no such gate today, so they stay open.
  const isAccessible = (suite: SuiteInfo) => {
    if (suite.path === null) return false;
    if (suite.id === "marketing") return hasMarketingAccess(orgRole);
    if (suite.id === "crm") return hasCrmAccess(role, orgRole);
    return true;
  };

  const handleSelect = (suite: SuiteInfo) => {
    if (suite.id === active.id) {
      setDropdownOpen(false);
      return;
    }
    if (suite.path === null) {
      toast("Convo Automate is coming soon.");
      return;
    }
    if (!isAccessible(suite)) {
      toast.error(`Your role doesn't have access to Convo ${suite.label}.`);
      return;
    }
    setDropdownOpen(false);
    navigate(suite.path);
  };

  const handleTriggerClick = () => {
    if (!open) {
      onExpandSidebar();
      setTimeout(() => setDropdownOpen(true), 300);
      return;
    }
    setDropdownOpen((s) => !s);
  };

  // Every other sidebar is white in light mode / dark-gray in dark mode, so
  // the trigger's own dark: classes already give it the right contrast
  // either way. Convo CXO's sidebar is a fixed dark rail regardless of the
  // app's light/dark mode (see cxo-sidebar/sidebar.tsx) — on that one, the
  // trigger needs to read as white always, not just when dark mode happens
  // to be on, or it's invisible in light mode.
  const isCxo = activeSuite === "cxo";

  return (
    <>
      {open ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={handleTriggerClick}
          className={`flex items-center gap-1 -ml-1.5 px-1.5 py-1 rounded-md transition-colors ${
            isCxo ? "hover:bg-white/10" : "hover:bg-gray-50 dark:hover:bg-gray-800"
          }`}
        >
          <span
            className={`text-sm font-semibold flex items-center gap-1 tracking-tight whitespace-nowrap ${isCxo ? "text-white" : "text-gray-800 dark:text-white"}`}
          >
            Convo <span className="text-blue-500 font-semibold">{active.label}</span>
          </span>
          <ChevronDown
            size={14}
            className={`font-semibold transition-transform shrink-0 ${isCxo ? "text-gray-300" : "text-[#7B7B7B] dark:text-gray-500"} ${
              dropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={handleTriggerClick}
          className={`inline-flex items-center justify-center w-7 h-7 rounded-md font-semibold transition-colors ${
            isCxo ? "text-gray-300 hover:text-white hover:bg-white/10" : "text-[#7B7B7B] dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          }`}
          title="Switch suite"
        >
          <LayoutGrid className="w-5 h-5" />
        </button>
      )}

      {dropdownOpen &&
        open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: "fixed", top: coords.top, left: coords.left }}
            className="w-[447px] z-[9999] rounded-xl border border-[#D9E2F0] dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg p-1.5 space-y-0.5"
          >
            {SUITES.map((suite) => {
            const isActive = suite.id === active.id;
            const accessible = isAccessible(suite);
            return (
              <button
                key={suite.id}
                type="button"
                onClick={() => handleSelect(suite)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                  isActive
                    ? "bg-[#F4F5F9] dark:bg-blue-500/10 border-[#4338CA33] dark:border-blue-500/20"
                    : accessible
                    ? "border-transparent hover:bg-gray-50 dark:hover:bg-gray-800"
                    : "border-transparent opacity-50 cursor-not-allowed"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900  dark:text-gray-100">
                    Convo <span className="text-blue-500">{suite.label}</span>
                  </span>
                  {isActive && (
                    <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                      <Check size={10} className="text-white" strokeWidth={3} />
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-[12px] font-semibold text-gray-500 dark:text-gray-400 leading-snug">{suite.description}</p>
              </button>
            );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default SuiteSwitcher;
